// Task types (docs/12 "Task types", D53): the admin-managed categories workflows and task groups
// belong to. A type that workflows use cannot be deleted; the API says so and the dialog shows it.
import { PencilSimple, Plus, Tag, Trash } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ActionIcon, Button, Tooltip } from "rizzui";

import { createTaskType, deleteTaskType, taskTypesQuery, updateTaskType } from "../catalog/api";
import { CatalogIcon } from "../catalog/icons";
import { SearchBox } from "../catalog/SearchBox";
import { useUrlSearch } from "../catalog/useUrlSearch";
import { TaskTypeDrawer } from "../catalog/TaskTypeDrawer";
import type { TaskType, TaskTypeInput } from "../catalog/types";
import { type Messages, useT } from "../i18n";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { type Column, DataTable } from "../ui/DataTable";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, Pill } from "../ui/status";

function columns({ catalog, common }: Messages, edit: (t: TaskType) => void, remove: (t: TaskType) => void): Column<TaskType>[] {
  const m = catalog.taskTypes;
  return [
    {
      key: "name",
      header: m.columns.name,
      cell: (t) => (
        <div className="flex items-center gap-3">
          <CatalogIcon name={t.icon} className="hidden sm:flex" />
          <div className="min-w-0">
            <div className="truncate font-semibold text-gray-900">{t.name}</div>
            <Mono className="text-xs text-gray-500">{t.slug}</Mono>
          </div>
        </div>
      ),
    },
    { key: "description", header: m.columns.description, hideBelow: "lg", cell: (t) => <span className="text-gray-600">{t.description || "—"}</span> },
    { key: "order", header: m.columns.order, hideBelow: "md", cell: (t) => <span className="tabular">{t.sort_order}</span> },
    { key: "enabled", header: m.columns.enabled, cell: (t) => (t.enabled ? <Pill tone="green">{m.enabled}</Pill> : <Pill tone="gray">{m.disabled}</Pill>) },
    {
      key: "actions",
      header: "",
      className: "text-end",
      cell: (t) => (
        <div className="flex justify-end gap-1.5">
          <Tooltip content={common.actions.edit} placement="top">
            <ActionIcon size="sm" variant="outline" aria-label={m.edit(t.name)} onClick={() => edit(t)}>
              <PencilSimple weight="bold" className="h-4 w-4" />
            </ActionIcon>
          </Tooltip>
          <Tooltip content={common.actions.delete} placement="top">
            <ActionIcon size="sm" variant="outline" aria-label={m.deleteName(t.name)} className="hover:border-red hover:text-red" onClick={() => remove(t)}>
              <Trash weight="bold" className="h-4 w-4" />
            </ActionIcon>
          </Tooltip>
        </div>
      ),
    },
  ];
}

export default function TaskTypesPage() {
  const messages = useT();
  const t = messages.catalog.taskTypes;
  const crumbs = sectionCrumbs("taskTypes");
  const search = useUrlSearch();
  const query = useQuery({ ...taskTypesQuery(search.q), placeholderData: (previous) => previous });
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState<{ editing?: TaskType }>();
  const [deleting, setDeleting] = useState<TaskType>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["catalog", "task-types"] });

  async function save(input: TaskTypeInput) {
    const editing = drawer?.editing;
    const saved = editing ? await updateTaskType(editing.id, input) : await createTaskType(input);
    await refresh();
    toast(editing ? t.saved(saved.name) : t.created(saved.name));
  }

  async function remove(type: TaskType) {
    await deleteTaskType(type.id);
    await refresh();
    toast(t.deleted(type.name));
  }

  return (
    <>
      <PageHeader title={SECTIONS.taskTypes.title} breadcrumb={crumbs} description={SECTIONS.taskTypes.subtitle}>
        <Button className="gap-1.5" onClick={() => setDrawer({})}>
          <Plus weight="bold" className="h-4 w-4" /> {t.new}
        </Button>
      </PageHeader>
      <SearchBox className="mb-4 md:w-80" label={t.search} placeholder={t.searchPlaceholder} text={search.text} onChange={search.setText} />
      <div className={cn(cardClass, "p-3 lg:p-4")}>
        {query.isPending ? <Loading /> : null}
        {query.isError ? <ErrorState title={t.loadError} message={query.error.message} /> : null}
        {query.data?.length === 0 ? (
          <EmptyState compact icon={<Tag weight="duotone" />} title={search.q ? t.nothingMatches : t.empty}>
            {search.q ? t.noMatch(search.q) : t.emptyHelp}
          </EmptyState>
        ) : null}
        {query.data && query.data.length > 0 ? (
          <DataTable label={t.title} columns={columns(messages, (type) => setDrawer({ editing: type }), setDeleting)} rows={query.data} rowKey={(t) => t.id} />
        ) : null}
      </div>
      <TaskTypeDrawer open={drawer !== undefined} editing={drawer?.editing} onClose={() => setDrawer(undefined)} onSave={save} />
      <ConfirmDialog
        open={deleting !== undefined}
        label={t.deleteTitle(deleting?.name ?? "")}
        title={t.deleteTitle(deleting?.name ?? "")}
        confirmLabel={messages.common.actions.delete}
        onClose={() => setDeleting(undefined)}
        onConfirm={async () => deleting && remove(deleting)}
      >
        {t.deleteHelp}
      </ConfirmDialog>
    </>
  );
}
